"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";

import {
	ProductCarousel,
	type ProductCarouselProps
} from "@/composite/ProductCarousel";
import type { ProductsResponse } from "@/domain/product";
import type { ProductReference } from "@/types/references";
import { resolveImageProps } from "@/app/utilities/images";

import type { ProductCarouselBlockProps } from ".";

export interface ProductCarouselBlockClientProps extends ProductCarouselBlockProps {
	productsData: ProductReference[];
	productsResponse: ProductsResponse;
}

/**
 * Featured Card block component
 */
export function ProductCarouselBlock({
	product_carousel_heading_tag,
	product_carousel_title,
	cta,
	productsData,
	productsResponse
}: ProductCarouselBlockClientProps): JSX.Element {
	if (productsResponse?.type === "failure") {
		throw new Error(productsResponse.message);
	}

	return (
		<ProductCarousel<NextImageProps, NextLinkProps>
			title={{
				children: product_carousel_title ?? "",
				styledAs:
					product_carousel_heading_tag as ProductCarouselProps["title"]["styledAs"]
			}}
			cta={{
				children: cta?.cta_title ?? "",
				href: cta?.cta_link?.href ?? "",
				"aria-label": cta?.cta_assistive_text ?? "",
				component: NextLink
			}}
			products={
				productsData?.map((product) => {
					const productData = productsResponse?.products?.find(
						({ sku }) => sku === product.sku
					);

					return {
						image: {
							...resolveImageProps("1:1"),
							component: NextImage,
							src: productData?.image ?? "",
							alt: productData?.name ?? ""
						},
						title: {
							children: product?.title ?? "",
							href: product?.meta_data?.canonical_url?.href ?? ""
						},
						rating: {
							rating: productData?.starsRating ?? 0,
							label: productData?.numRatings
								? `(${productData?.numRatings})`
								: ""
						},
						price: {
							display: productData?.salePrice ?? 0
						}
					};
				}) ?? []
			}
		/>
	);
}
